/**
 * Workspace-level clipboard for canvas resources. Mounted in `WorkspaceLayout`,
 * above the per-canvas `<ReactFlowProvider key={canvasId}>` remount boundary, so
 * copied nodes survive switching canvas tabs. Mirrored to `sessionStorage`
 * (session-only). Stored as an array so a future multi-entry tray needs no
 * reshape; V1 keeps one entry (`CLIPBOARD_MAX_ENTRIES = 1`).
 */

import type { Edge, Node } from "@xyflow/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

const STORAGE_KEY = "canvas-clipboard";
const CLIPBOARD_MAX_ENTRIES = 1;

export interface CanvasClipboardEntry {
  /** Copied React Flow nodes. Sanitized + re-IDed at paste time, not here. */
  nodes: Node[];
  /** Edges whose endpoints are both in `nodes` — re-IDed/remapped at paste time. */
  edges: Edge[];
  /**
   * Canvas the nodes were copied from. Drives the cross-canvas true-copy of
   * file-backed entity nodes on paste; null when unknown (degrades to a plain
   * reference copy).
   */
  sourceCanvasId: string | null;
  /** Epoch ms — ordering / display. */
  copiedAt: number;
}

export interface CanvasClipboardContextValue {
  entries: CanvasClipboardEntry[];
  /** Most recently copied entry, or null. */
  latest: CanvasClipboardEntry | null;
  /** Replace/prepend the clipboard with a new entry (copy / cut). */
  push: (entry: CanvasClipboardEntry) => void;
  /** Empty the clipboard. */
  clear: () => void;
}

export const CanvasClipboardContext =
  createContext<CanvasClipboardContextValue | null>(null);

function readStored(): CanvasClipboardEntry[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (e) =>
          !!e &&
          Array.isArray(e.nodes) &&
          typeof e.copiedAt === "number" &&
          (e.sourceCanvasId === null || typeof e.sourceCanvasId === "string"),
      )
      .map(
        (e): CanvasClipboardEntry => ({
          ...e,
          edges: Array.isArray(e.edges) ? e.edges : [],
        }),
      );
  } catch {
    return [];
  }
}

function writeStored(entries: CanvasClipboardEntry[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // quota exceeded / unavailable — in-memory state still works this session.
  }
}

export function CanvasClipboardProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<CanvasClipboardEntry[]>(() =>
    readStored(),
  );

  // Mirror to sessionStorage as a side effect of state changing — not inside the
  // setState updater (which StrictMode double-invokes).
  useEffect(() => {
    writeStored(entries);
  }, [entries]);

  const push = useCallback((entry: CanvasClipboardEntry) => {
    setEntries((prev) => [entry, ...prev].slice(0, CLIPBOARD_MAX_ENTRIES));
  }, []);

  const clear = useCallback(() => setEntries([]), []);

  const value = useMemo<CanvasClipboardContextValue>(
    () => ({ entries, latest: entries[0] ?? null, push, clear }),
    [entries, push, clear],
  );

  return (
    <CanvasClipboardContext.Provider value={value}>
      {children}
    </CanvasClipboardContext.Provider>
  );
}

/** Strict accessor (throws if no provider) — for the V2 clipboard tray UI. */
export function useCanvasClipboard(): CanvasClipboardContextValue {
  const ctx = useContext(CanvasClipboardContext);
  if (!ctx) {
    throw new Error(
      "useCanvasClipboard must be used within CanvasClipboardProvider",
    );
  }
  return ctx;
}
